import React from 'react'
import { Row, Col, Modal, Tab, Nav } from 'react-bootstrap'
import '../ModalStyle.css'
import PatientDetails from './PatientDetails'
import PatientTasks from './PatientTasks'
import PatientStats from './PatientStats'
import PatientActivity from './PatientActivity'
import PatientDocuments from './PatientDocuments'
import { Patient } from '@dataTypes/PatientInfo.types'

type ExtendedPatient = {
  dob?: string
  phone?: string
  work_phone?: string
  fax?: string
  ssn?: string
  address_1?: string
  address_2?: string
  city?: string
  state?: string
  zip?: string
  marital_status?: string
  chart_number?: string
  financing?: string
  created_date?: string
  future_visit?: string
  balance_due?: number
}

type PatientCardProps = {
  show: boolean
  onHide: () => void
  patient?: (Patient & ExtendedPatient) | null
}

const PatientCard: React.FC<PatientCardProps> = ({ show, onHide, patient }) => {
  if (!patient) {
    return null
  }

  const patientWithFallbacks = {
    ...patient,
    dob: patient.dob || patient.date_of_birth,
    phone: patient.phone || patient.cellphone,
    work_phone: patient.work_phone || 'N/A',
    fax: patient.fax || 'N/A',
    ssn: patient.ssn || 'N/A',
    address_1: patient.address || 'N/A',
    address_2: patient.address_2 || '',
    city: patient.city || 'N/A',
    state: patient.state || 'N/A',
    zip: patient.zip || 'N/A',
    marital_status: patient.marital_status || 'N/A',
    chart_number: patient.chart_number || 'N/A',
    financing: patient.financing || 'N/A',
    created_date: patient.created_date || '',
    future_visit: patient.date_next_visit || '',
    // age: String(patient.age),
    hygienist: 'N/A',
    dentist: 'N/A',
  }

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        backdrop="static"
        keyboard={false}
        className="modFull"
      >
        <Modal.Header className="bg-custom3 br-0 border-0" closeButton>
          <Modal.Title className="fs-5 text-dark">Patient card</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-custom3 flexI">
          <Row>
            <Col md="5">
              <div className="d-flex align-items-start">
                <div className="ptCardImage">
                  <img
                    src={patient.photo}
                    width="80"
                    height="80"
                    className="d-inline-block"
                    alt={`${patient.first_name} ${patient.last_name}`}
                  />
                </div>
                <div className="ms-3">
                  <h3 className="text-dark">
                    {`${patient.first_name} ${patient.last_name}`}
                  </h3>
                  <p className="text-dark">Patient</p>
                </div>
              </div>
            </Col>
            <Col md="7" className="text-end">
              {/* Actions */}
              <div className="pcm-actions">
                <a href="">
                  <i className="bi bi-three-dots-vertical"></i>
                </a>
                <button
                  type="button"
                  className="unstyled-button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  aria-label="Chat actions"
                >
                  <i className="bi bi-chat-left-dots-fill" />
                </button>
                <ul
                  className="dropdown-menu"
                  aria-labelledby="navbarDropdownMenuLink"
                >
                  <li>
                    <a className="dropdown-item" href="#">
                      Communications &raquo;
                    </a>
                    <ul className="dropdown-menu dropdown-submenu">
                      <li>
                        <a className="dropdown-item" href="#">
                          Send a direct message
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Send an email campaign
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Send a financing officer
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Invite to a virtual consult
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Send a voicemail
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Invite to schedule
                        </a>
                      </li>
                    </ul>
                  </li>
                  <li>
                    <a className="dropdown-item" href="#">
                      Forms &raquo;
                    </a>
                    <ul className="dropdown-menu dropdown-submenu">
                      <li>
                        <a className="dropdown-item" href="#">
                          Prepare forms and consents
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Prepare a treatment plan
                        </a>
                      </li>
                      <li>
                        <a className="dropdown-item" href="#">
                          Prepare a consent (deprecated)
                        </a>
                      </li>
                    </ul>
                  </li>
                </ul>
                <a href="">
                  <i className="bi bi-calendar2-plus-fill"></i>
                </a>
                <a href="">
                  <i className="bi bi-shield-fill-plus"></i>
                </a>
                <a href="">
                  <i className="bi bi-telephone-fill"></i>
                </a>
                <a href="">
                  <i className="fa-solid fa-arrows-rotate"></i>
                </a>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Row>
          <Col>
            <Tab.Container defaultActiveKey="first">
              <Row>
                <Col sm={12}>
                  <Nav variant="pills" className="bg-custom3 br-0 ptCardNav">
                    <Nav.Item>
                      <Nav.Link eventKey="first">Details</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="second">Tasks</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="third">Stats</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="fourth">Activity</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="fifth">Documents</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>
              </Row>
              <Row>
                <Col sm={12}>
                  <Tab.Content className="pcTab">
                    <PatientDetails patient={patientWithFallbacks} />
                    <PatientTasks />
                    <PatientStats patient={patientWithFallbacks} />
                    <PatientActivity />
                    <PatientDocuments />
                  </Tab.Content>
                </Col>
              </Row>
            </Tab.Container>
          </Col>
        </Row>
      </Modal>
    </>
  )
}

export default PatientCard
